// Live reviewer-pasted evidence for a survey, read straight from
// qa_items_evidence — because the QA server copies it into
// property_attributes.prod_data only when a decision is saved, so evidence
// added after approval never reaches prod_data (which the export reads).
//
// Both tables are RLS-gated to QA reviewers (is_qa_reviewer); a non-reviewer
// export simply gets no rows, so this degrades to the prod_data-only behavior
// rather than erroring. The join is exact: qa_items.field_path IS the
// prod_data question-id key, so callers attribute evidence with the question
// metadata already in prod_data.

/** questionId (field_path / prod_data key) → reviewer evidence items (raw jsonb). */
export async function fetchReviewerEvidence(
  surveyId: string
): Promise<Map<string, unknown[]>> {
  const out = new Map<string, unknown[]>();
  if (!surveyId) return out;

  // Lazy import: the supabase client pulls in the Firebase client, which
  // self-initializes storage and throws outside a browser (unit tests). It
  // must not load until an export actually runs.
  const { supabase } = await import("@/shared/integrations/supabase/client");

  const { data: items, error: itemsErr } = await supabase
    .from("qa_items")
    .select("id, field_path")
    .eq("target_kind", "property_attribute")
    .eq("target_id", surveyId);
  if (itemsErr || !items?.length) return out; // RLS-empty or none: prod_data only

  const rows = items as Array<{ id: string; field_path: string }>;
  const fieldPathByItem = new Map(rows.map((r) => [r.id, r.field_path]));

  const { data: ev, error: evErr } = await supabase
    .from("qa_items_evidence")
    .select("qa_item_id, ref")
    .eq("kind", "reviewer_evidence")
    .in(
      "qa_item_id",
      rows.map((r) => r.id)
    );
  if (evErr || !ev?.length) return out;

  for (const row of ev as Array<{ qa_item_id: string; ref: unknown }>) {
    const field = fieldPathByItem.get(row.qa_item_id);
    if (!field || !Array.isArray(row.ref)) continue;
    const list = out.get(field) ?? [];
    list.push(...row.ref);
    out.set(field, list);
  }
  return out;
}

// The pure merge moved to the shared engine (the edge function uses it too).
export { mergeReviewerEvidence } from "@excel-export/reviewerEvidence.ts";
